import { useMemo, useState } from "react";
import axios from "axios";
import { Check, Heart, Lock, Minus, Plus, Save, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { appTabs, fsmData, marketplaceItems, operations, IMG } from "@/data/vomarcData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roomColors = [
  { id: "coral", label: "Coral", value: "#d76557" },
  { id: "sage", label: "Sage", value: "#6e9d74" },
  { id: "sky", label: "Sky", value: "#b9dbe9" },
  { id: "cream", label: "Cream", value: "#efd6a9" },
];

export function FeatureAppModel() {
  const [activeTab, setActiveTab] = useState("home");
  const [roomColor, setRoomColor] = useState(roomColors[0]);
  const [selectedProduct, setSelectedProduct] = useState(marketplaceItems[0]);
  const [cart, setCart] = useState([]);
  const [completedTasks, setCompletedTasks] = useState(["scan"]);
  const [scanActive, setScanActive] = useState(false);
  const [note, setNote] = useState("Living room makeover with smart lighting and warm color palette.");
  const [saving, setSaving] = useState(false);

  const progress = useMemo(() => Math.round((completedTasks.length / operations.length) * 100), [completedTasks]);

  const toggleTask = (taskId) => {
    setCompletedTasks((current) => current.includes(taskId) ? current.filter((id) => id !== taskId) : [...current, taskId]);
  };

  const addToCart = (item) => {
    setSelectedProduct(item);
    setCart((current) => current.includes(item.id) ? current : [...current, item.id]);
    toast.success(`${item.item} added to room plan`);
  };

  const saveState = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/app-state`, {
        active_tab: activeTab,
        room_color: roomColor.label,
        selected_product: selectedProduct.item,
        project_progress: progress,
        completed_tasks: completedTasks,
        notes: note,
      });
      toast.success("App changes saved successfully");
    } catch {
      toast.error("Unable to save app changes right now");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="app-model-grid" data-testid="working-app-model">
      <div className="phone-frame" data-testid="pitch-mobile-app-frame">
        <div className="phone-status" data-testid="phone-status-bar"><span>9:41</span><span>5G ◔</span></div>
        <div className="phone-screen" style={{ "--room-color": roomColor.value }} data-testid="phone-screen-content">
          {activeTab === "home" && (
            <div className="app-view dashboard-view" data-testid="app-tab-home-view">
              <div className="app-tab-banner" data-testid="home-banner">
                <img src={IMG.hero} alt="VOMARC project thumbnail" />
                <span className="app-banner-tag">LIVE PROJECT</span>
              </div>
              <h3 data-testid="home-view-title">VOMARC Home</h3>
              <p data-testid="home-view-copy">Your 3BHK project is ready to transform.</p>
              <div className="dash-hero-card" data-testid="home-dashboard-hero-card">
                <span data-testid="home-project-id">{fsmData.projectId}</span>
                <strong data-testid="home-project-title">{fsmData.project}</strong>
                <div className="dash-progress"><i style={{ width: `${progress}%` }} /></div>
              </div>
              <div className="mini-metrics" data-testid="home-stats-row"><span>3 Rooms</span><span>12 Items</span><span>{fsmData.budget}</span></div>
              <button className="scan-action" onClick={() => setActiveTab("ar")} data-testid="home-open-ar-button"><ScanLine size={18} /> Open AR Designer</button>
            </div>
          )}

          {activeTab === "flat" && (
            <div className="app-view flat-view" data-testid="app-tab-flat-view">
              <div className="app-tab-banner" data-testid="flat-banner">
                <img src={IMG.scan} alt="VOMARC LiDAR floor mesh" />
                <span className="app-banner-tag">LIDAR FLOOR · 3.07 m</span>
              </div>
              <h3 data-testid="flat-view-title">Flat overview</h3>
              <div className="mini-floorplan" data-testid="flat-floorplan-visual"><span>Living</span><span>Kitchen</span><span>Bedroom</span><span>Bath</span></div>
              <div className="room-list" data-testid="flat-room-list">
                {fsmData.rooms.map((room) => (
                  <button key={room.name} className="room-row" onClick={() => setActiveTab("ar")} data-testid={`flat-room-button-${room.name.toLowerCase().replaceAll(" ", "-")}`}>
                    <strong>{room.name}</strong><em>{room.area}</em><span>{room.status}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === "ar" && (
            <div className="app-view design-view" data-testid="app-tab-ar-view">
              <div className="editable-room" data-testid="editable-room-preview">
                <img className="ar-room-photo" src={IMG.arRoom} alt="VOMARC AR live room preview with selected wall color" data-testid="ar-room-photo" />
                <div className="ui-wall" data-testid="design-room-color-layer" />
                {scanActive && <div className="ar-scan-sweep" data-testid="ar-scan-sweep" />}
                <span className="ar-pin pin-sofa" data-testid="ar-pin-sofa">Sofa · {selectedProduct.price}</span>
                <span className="ar-pin pin-paint" data-testid="ar-pin-paint">Paint fit 92%</span>
                <span className="measure-line" data-testid="ar-measure-line">3.8 m wall</span>
                <span className="ar-corner-tag tl" data-testid="ar-corner-tl">VOMARC · LIVE AR</span>
                <span className="ar-corner-tag br" data-testid="ar-corner-br">{roomColor.label}</span>
              </div>
              <button className={`scan-action ${scanActive ? "active" : ""}`} onClick={() => setScanActive(!scanActive)} data-testid="scan-toggle-button"><ScanLine size={18} /> {scanActive ? "AR scan live" : "Start AR scan"}</button>
              <h3 data-testid="design-view-title">Edit room style</h3>
              <div className="swatch-row" data-testid="room-color-controls">
                {roomColors.map((color) => (
                  <button key={color.id} className={`color-swatch ${roomColor.id === color.id ? "selected" : ""}`} style={{ background: color.value }} onClick={() => setRoomColor(color)} data-testid={`room-color-button-${color.id}`} aria-label={color.label} />
                ))}
              </div>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="design-note-textarea" />
            </div>
          )}

          {activeTab === "order" && (
            <div className="app-view market-view" data-testid="app-tab-market-view">
              <h3 data-testid="market-view-title">Order + marketplace</h3>
              <p data-testid="market-selected-product">Selected: {selectedProduct.item}</p>
              <div className="service-toggle" data-testid="service-option-row"><button>Deliver</button><button className="active">Install</button><button>Full rework</button></div>
              <div className="mobile-products" data-testid="mobile-product-list">
                {marketplaceItems.map((item) => (
                  <button key={item.id} className={`mobile-product ${selectedProduct.id === item.id ? "active" : ""}`} onClick={() => addToCart(item)} data-testid={`mobile-product-button-${item.id}`}>
                    <img className="mobile-product-thumb" src={item.image} alt={item.item} loading="lazy" data-testid={`mobile-product-thumb-${item.id}`} />
                    <div className="mobile-product-text">
                      <span data-testid={`mobile-product-name-${item.id}`}>{item.item}</span>
                      <em data-testid={`mobile-product-fit-${item.id}`}>{item.fit}</em>
                      <strong data-testid={`mobile-product-price-${item.id}`}>{item.price}</strong>
                    </div>
                    <Plus size={15} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === "track" && (
            <div className="app-view fsm-view" data-testid="app-tab-fsm-view">
              <div className="app-tab-banner" data-testid="track-banner">
                <img src={IMG.workforce} alt="VOMARC FSM workforce on site" />
                <span className="app-banner-tag">FSM · GPS LIVE</span>
              </div>
              <h3 data-testid="fsm-view-title">Track execution</h3>
              <div className="stage-pipeline" data-testid="tracker-stage-pipeline">{fsmData.stages.map((stage, index) => <span key={stage} className={index < 3 ? "done" : ""}>{stage}</span>)}</div>
              <div className="progress-ring" data-testid="fsm-progress-ring"><strong>{progress}%</strong><span>Project progress</span></div>
              {operations.map((task) => (
                <button key={task.id} className="task-row" onClick={() => toggleTask(task.id)} data-testid={`fsm-task-button-${task.id}`}>
                  <span className={completedTasks.includes(task.id) ? "done-dot" : "todo-dot"}>{completedTasks.includes(task.id) ? <Check size={14} /> : <Minus size={14} />}</span>
                  <span data-testid={`fsm-task-label-${task.id}`}>{task.label}</span>
                </button>
              ))}
              <div className="vault-mini-strip" data-testid="mobile-vault-card"><Lock size={18} /><span>Warranty vault • Site photos • Payment milestones</span></div>
              <div className="vault-list" data-testid="mobile-vault-list"><span>Room scan v1</span><span>Color version: {roomColor.label}</span><span>Cart items: {cart.length}</span><span>FSM progress: {progress}%</span></div>
            </div>
          )}
        </div>
        <div className="bottom-tabs" data-testid="mobile-bottom-tabs">
          {appTabs.map((tab) => (
            <button key={tab.id} className={activeTab === tab.id ? "active" : ""} onClick={() => setActiveTab(tab.id)} data-testid={`mobile-tab-button-${tab.id}`}><span>{tab.icon}</span><em>{tab.label}</em></button>
          ))}
        </div>
      </div>

      <div className="app-control-panel" data-testid="app-control-panel">
        <span className="eyebrow" data-testid="app-control-eyebrow">Working model</span>
        <h2 data-testid="app-control-title">A pitch-ready app model for dashboard, AR design, ordering and tracking.</h2>
        <p data-testid="app-control-copy">Use the product tabs. Home shows the project, Flat opens room planning, AR edits color and notes, Order adds products, and Track updates FSM progress.</p>
        <div className="control-summary" data-testid="app-control-summary">
          <div><span>Active tab</span><strong>{activeTab}</strong></div>
          <div><span>Wall color</span><strong>{roomColor.label}</strong></div>
          <div><span>Selected product</span><strong>{selectedProduct.item}</strong></div>
          <div><span>FSM progress</span><strong>{progress}%</strong></div>
        </div>
        <Button className="primary-cta save-app" onClick={saveState} disabled={saving} data-testid="save-app-state-button"><Save size={18} /> {saving ? "Saving..." : "Save app changes"}</Button>
      </div>
    </div>
  );
}

export function ClickableFeatureCards({ activeFeature, setActiveFeature, features }) {
  const active = features.find((feature) => feature.id === activeFeature) || features[0];
  return (
    <div className="feature-tab-model" data-testid="feature-tab-model">
      <div className="feature-buttons" data-testid="feature-tab-buttons">
        {features.map((feature) => (
          <button key={feature.id} className={activeFeature === feature.id ? "active" : ""} onClick={() => setActiveFeature(feature.id)} data-testid={`feature-tab-button-${feature.id}`}>{feature.label}</button>
        ))}
      </div>
      <div className="feature-showcase" data-testid="feature-showcase">
        <PlatformVisual type={active.id} />
        <div className="feature-showcase-copy" data-testid="feature-showcase-copy">
          <span data-testid="feature-showcase-label">{active.label}</span>
          <h3 data-testid="feature-showcase-title">{active.title}</h3>
          <p data-testid="feature-showcase-text">{active.copy}</p>
          <button data-testid="feature-showcase-action"><Heart size={16} /> Explore {active.label}</button>
        </div>
      </div>
    </div>
  );
}

export function PlatformVisual({ type = "home" }) {
  return (
    <div className={`platform-visual platform-${type}`} data-testid="feature-showcase-image">
      <div className="visual-topbar"><span>VOMARC OS</span><em>{type.toUpperCase()}</em></div>
      {type === "home" && <><div className="visual-hero"><strong>3BHK Rework</strong><span>2 active projects • 5 rooms designed</span></div><div className="visual-grid"><i /><i /><i /></div></>}
      {type === "flat" && <><div className="visual-plan"><span>Living</span><span>Kitchen</span><span>Bedroom</span><span>Bath</span></div><div className="visual-grid"><i /><i /></div></>}
      {type === "ar" && <><div className="visual-room"><b className="scan-line" /><span className="pin-a">Sofa fit</span><span className="pin-b">Wall tint</span></div><div className="visual-swatches"><i /><i /><i /><i /></div></>}
      {type === "order" && <><div className="visual-grid products"><i /><i /><i /></div><div className="bid-card"><strong>Best vendor bid</strong><span>₹1.87L • 6 days</span></div></>}
      {type === "track" && <><div className="visual-pipeline"><i /><i /><i /><i /></div><div className="worker-card"><strong>GPS verified</strong><span>Worker on-site • QC pending</span></div><div className="visual-grid"><i /><i /></div></>}
    </div>
  );
}